/**
 * VÉRAULT Portal — Send Dossier modal (Phase 1.4)
 *
 * Three-step flow:
 *   1. Compose  — pick recipient, custom note, Suite Door toggle, then
 *                 tick the client-visible records to include. Records
 *                 with visibility advisor/internal/archived show in a
 *                 dimmed "Excluded by visibility" list and CANNOT be
 *                 toggled on. This is the hard visibility gate.
 *   2. Preview  — read-only summary of the final bundle (recipient,
 *                 records included, records excluded, note text). The
 *                 advisor confirms before anything is queued.
 *   3. Sent     — calm success card with bundle id, status (mock/preview
 *                 by default), and a link to the Email Log.
 *
 * The backend already enforces the same visibility rule; this modal
 * just keeps the advisor calm by surfacing it upfront.
 */
import React from "react";
import { X, Eye, EyeOff, Send, ChevronRight, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { Surface, Heading, StatusTag, PortalButton, api } from "../../../index";
import { toast } from "sonner";

const NON_CLIENT_VISIBLE = new Set(["advisor", "internal", "archived"]);

export default function SendDossierModal({ scope, target, onClose }) {
  // scope: "trip" | "client" · target: {id, client_id, title, email}
  const [step, setStep] = React.useState("compose"); // compose | preview | sent
  const [records, setRecords] = React.useState([]);
  const [selectedIds, setSelectedIds] = React.useState(new Set());
  const [toEmail, setToEmail] = React.useState(target?.email || "");
  const [note, setNote] = React.useState("");
  const [includeSuiteDoor, setIncludeSuiteDoor] = React.useState(scope === "trip");
  const [loading, setLoading] = React.useState(true);
  const [sending, setSending] = React.useState(false);
  const [result, setResult] = React.useState(null);

  React.useEffect(() => {
    const path = scope === "client" ? `/dossier/client/${target.id}` : `/dossier/trip/${target.id}`;
    api.get(path)
      .then(({ data }) => {
        setRecords(data.records || []);
        // Pre-select all client-visible records.
        const initial = new Set(
          (data.records || [])
            .filter((r) => !NON_CLIENT_VISIBLE.has(r.visibility))
            .map((r) => r.id)
        );
        setSelectedIds(initial);
      })
      .catch(() => setRecords([]))
      .finally(() => setLoading(false));
  }, [scope, target?.id]);

  const visible = records.filter((r) => !NON_CLIENT_VISIBLE.has(r.visibility));
  const hidden  = records.filter((r) =>  NON_CLIENT_VISIBLE.has(r.visibility));
  const selectedRecords = visible.filter((r) => selectedIds.has(r.id));

  const toggleId = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const send = async () => {
    setSending(true);
    try {
      const path = scope === "client"
        ? `/dossier/client/${target.id}/send`
        : `/dossier/trip/${target.id}/send`;
      const { data } = await api.post(path, {
        to_email: toEmail,
        record_ids: Array.from(selectedIds),
        include_suite_door: includeSuiteDoor,
        custom_note: note,
      });
      setResult(data);
      setStep("sent");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Send failed.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="send-dossier-modal"
    >
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-lg shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-[rgba(0,0,0,0.06)]">
          <div className="min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent-gold))]">
              Send Dossier · {step}
            </p>
            <Heading level="h3" className="mt-1">{target?.title || "Dossier"}</Heading>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
            data-testid="send-dossier-close"
          >
            <X className="size-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="size-5 text-[hsl(var(--foreground-muted))] animate-spin" strokeWidth={1.5} />
            </div>
          ) : step === "compose" ? (
            <>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-1">
                  Recipient
                </label>
                <input
                  type="email"
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  placeholder="client@example.com"
                  className="w-full px-3 py-2 bg-white border border-[rgba(0,0,0,0.1)] rounded-md font-serif text-[14px] outline-none focus:border-[hsl(var(--accent-gold))]/40"
                  data-testid="send-dossier-recipient"
                />
              </div>

              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))] mb-2">
                  Client-visible records · {visible.length}
                </p>
                <Surface variant="default" padded={false} className="overflow-hidden">
                  {visible.length === 0 ? (
                    <p className="px-4 py-4 font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
                      Nothing client-visible to send yet.
                    </p>
                  ) : (
                    <ul className="divide-y divide-[rgba(0,0,0,0.04)] max-h-60 overflow-y-auto">
                      {visible.map((r) => (
                        <li key={r.id}>
                          <label className="px-3 py-2.5 flex items-start gap-3 cursor-pointer hover:bg-[hsl(var(--background-paper))]/60">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(r.id)}
                              onChange={() => toggleId(r.id)}
                              className="mt-1 size-3.5 rounded border-[rgba(0,0,0,0.2)] text-[hsl(var(--accent-gold))]"
                              data-testid={`send-dossier-toggle-${r.id}`}
                            />
                            <div className="min-w-0 flex-1">
                              <p className="font-serif text-[14px] text-[hsl(var(--foreground))] truncate">
                                {r.title}
                              </p>
                              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                                {r.kind} · {(r.status || "").replace(/_/g, " ")}
                              </p>
                            </div>
                            <Eye className="size-3 text-[hsl(168,22%,40%)] mt-1.5" strokeWidth={1.5} />
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </Surface>
              </div>

              {hidden.length > 0 && (
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-2">
                    Excluded by visibility · {hidden.length}
                  </p>
                  <Surface variant="paper" padded={false} className="overflow-hidden opacity-70">
                    <ul className="divide-y divide-[rgba(0,0,0,0.04)] max-h-32 overflow-y-auto">
                      {hidden.map((r) => (
                        <li key={r.id} className="px-3 py-2 flex items-center gap-3" data-testid={`send-dossier-excluded-${r.id}`}>
                          <EyeOff className="size-3 text-[hsl(var(--foreground-muted))] shrink-0" strokeWidth={1.5} />
                          <p className="flex-1 min-w-0 font-serif text-[13px] truncate">{r.title}</p>
                          <StatusTag tone="neutral">{r.visibility}</StatusTag>
                        </li>
                      ))}
                    </ul>
                  </Surface>
                  <p className="mt-1.5 font-accent italic text-[11px] text-[hsl(var(--foreground-muted))]">
                    Change a record&apos;s visibility to <em>client</em> in the Dossier to include it here.
                  </p>
                </div>
              )}

              <div className="flex items-center gap-3 flex-wrap">
                {scope === "trip" && (
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeSuiteDoor}
                      onChange={(e) => setIncludeSuiteDoor(e.target.checked)}
                      className="size-3.5 rounded border-[rgba(0,0,0,0.2)] text-[hsl(var(--accent-gold))]"
                      data-testid="send-dossier-suite-door"
                    />
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                      Include Suite Door link
                    </span>
                  </label>
                )}
              </div>

              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-1">
                  A note from you (optional)
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="Two or three calm lines, in your own voice…"
                  className="w-full px-3 py-2 bg-white border border-[rgba(0,0,0,0.1)] rounded-md font-serif text-[14px] outline-none focus:border-[hsl(var(--accent-gold))]/40 resize-none"
                  data-testid="send-dossier-note"
                />
              </div>
            </>
          ) : step === "preview" ? (
            <>
              <Surface variant="paper" padded>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">To</p>
                <p className="mt-1 font-serif text-[15px] text-[hsl(var(--foreground))]">{toEmail}</p>
              </Surface>
              <Surface variant="paper" padded>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                  Bundle · {selectedRecords.length} record{selectedRecords.length === 1 ? "" : "s"}
                </p>
                <ul className="mt-2 space-y-1" data-testid="send-dossier-preview-list">
                  {selectedRecords.map((r) => (
                    <li key={r.id} className="font-serif text-[14px] text-[hsl(var(--foreground))]">• {r.title}</li>
                  ))}
                </ul>
                {includeSuiteDoor && scope === "trip" && (
                  <p className="mt-3 font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
                    Suite Door link included.
                  </p>
                )}
                {note && (
                  <div className="mt-3 pt-3 border-t border-[rgba(0,0,0,0.06)]">
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Your note</p>
                    <p className="mt-1 font-serif text-[14px] whitespace-pre-wrap text-[hsl(var(--foreground))]">{note}</p>
                  </div>
                )}
              </Surface>
              <div className="flex items-start gap-2 px-1">
                <AlertCircle className="size-3.5 text-[hsl(var(--accent-gold))] mt-0.5" strokeWidth={1.5} />
                <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
                  If Resend isn&apos;t configured, this will be logged as a mock / preview email. No live email will leave the building.
                </p>
              </div>
            </>
          ) : (
            <>
              <Surface variant="paper" padded data-testid="send-dossier-success">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="size-5 text-[hsl(168,22%,40%)]" strokeWidth={1.5} />
                  <div className="min-w-0">
                    <p className="font-serif text-[16px] text-[hsl(var(--foreground))]">
                      {result?.communication?.mode === "live" ? "Bundle queued for delivery." : "Bundle logged in preview mode."}
                    </p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                      bundle · {result?.bundle_id} · {result?.communication?.status}
                    </p>
                    <p className="mt-2 font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
                      {result?.included?.length || 0} record{(result?.included?.length || 0) === 1 ? "" : "s"} included
                      {result?.excluded?.length ? ` · ${result.excluded.length} excluded by visibility` : ""}.
                    </p>
                  </div>
                </div>
              </Surface>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-[rgba(0,0,0,0.06)]">
          {step === "compose" && (
            <>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                {selectedRecords.length} selected
              </p>
              <PortalButton
                variant="primary"
                size="sm"
                disabled={!toEmail || selectedRecords.length === 0}
                onClick={() => setStep("preview")}
                icon={<ChevronRight className="size-3.5" strokeWidth={1.5} />}
                data-testid="send-dossier-to-preview"
              >Preview</PortalButton>
            </>
          )}
          {step === "preview" && (
            <>
              <PortalButton variant="ghost" size="sm" onClick={() => setStep("compose")} data-testid="send-dossier-back">
                Back
              </PortalButton>
              <PortalButton
                variant="primary"
                size="sm"
                disabled={sending}
                onClick={send}
                icon={<Send className="size-3.5" strokeWidth={1.5} />}
                data-testid="send-dossier-confirm"
              >
                {sending ? "Sending…" : "Send"}
              </PortalButton>
            </>
          )}
          {step === "sent" && (
            <PortalButton variant="primary" size="sm" onClick={onClose} data-testid="send-dossier-done">
              Done
            </PortalButton>
          )}
        </div>
      </div>
    </div>
  );
}
